import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { isNonEmptyString } from '@/utils/string';

import IconButton from '../Button/IconButton';

export type SelectFieldOption<T extends string> = { id: T; label: string };

export type SelectFieldProps<T extends string> = {
  value: T | '' | undefined;
  options: ReadonlyArray<SelectFieldOption<T>>;
  placeholder: string;
  className?: string;
  disabled?: boolean;
  onValueChange: (value: T) => void;
  /** クリアボタン押下時の処理（任意項目でのみ表示する想定） */
  onClear?: () => void;
  /** エラー状態（主にborder色切替用） */
  error?: boolean;
  'data-testid'?: string;
};

export const SelectField = <T extends string>({
  value,
  options,
  placeholder,
  className,
  disabled,
  onValueChange,
  onClear,
  error,
  'data-testid': testId,
}: SelectFieldProps<T>) => {
  const hasValue = isNonEmptyString(value);
  const selectedLabel = hasValue
    ? options.find((option) => option.id === value)?.label
    : undefined;
  const canClear = !disabled && !!onClear && hasValue;

  return (
    <Select
      value={value ?? ''}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <div className={cn('relative min-w-48 max-w-96', className)}>
        <SelectTrigger
          className={cn(
            'w-full overflow-hidden',
            canClear && 'pr-12',
            error && 'border-error-fill',
          )}
          aria-invalid={error ? true : undefined}
          data-testid={testId}
        >
          <div className="min-w-0 w-full flex-1 overflow-hidden text-start">
            <SelectValue
              placeholder={hasValue ? undefined : placeholder}
              aria-label={selectedLabel}
            >
              {selectedLabel != null ? (
                <span className="block min-w-0 w-full truncate text-start">
                  {selectedLabel}
                </span>
              ) : null}
            </SelectValue>
          </div>
        </SelectTrigger>

        {canClear && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton
              icon="XIcon"
              variant="mutedGhost"
              size="ExtraSmall"
              type="button"
              aria-label="Clear selection"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClear();
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        )}
      </div>

      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
